import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const usePasswordProtection = () => {
  const [isProtected, setIsProtected] = useState<boolean | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    // Always do server-side validation. localStorage is NOT a source of truth.
    if (!hasChecked.current) {
      setIsLoading(true);
      checkProtectionStatus();
      hasChecked.current = true;
    }
  }, []);

  const checkProtectionStatus = async () => {
    try {
      // Check if user is authenticated
      const { data: { session } } = await supabase.auth.getSession();
      
      let settings: any;
      let error: any;

      if (session?.user) {
        // Authenticated: direct table access
        const result = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_key', 'password_protection')
          .single();
        settings = result.data;
        error = result.error;
      } else {
        // Non-authenticated: use public RPC function
        const result = await supabase.rpc('get_public_settings');
        if (result.data) {
          const protectionSetting = result.data.find((s: any) => s.setting_key === 'password_protection');
          settings = protectionSetting ? { setting_value: protectionSetting.setting_value } : null;
        }
        error = result.error;
      }

      if (error) {
        console.error('Error fetching protection status:', error);
        setIsProtected(false);
        setIsLoading(false);
        return;
      }

      const enabled = (settings?.setting_value as { enabled?: boolean })?.enabled === true;
      setIsProtected(enabled);

      if (enabled) {
        // Always verify via server-side HttpOnly cookie. Never trust localStorage.
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const response = await fetch(`${supabaseUrl}/functions/v1/check-site-password-session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!response.ok) {
          console.error('Session check failed:', response.status);
          setIsAuthenticated(false);
          localStorage.removeItem('site_password_authenticated');
        } else {
          const data = await response.json();
          const authed = data?.authenticated === true;
          setIsAuthenticated(authed);
          if (!authed) {
            localStorage.removeItem('site_password_authenticated');
          }
        }
      } else {
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Error in checkProtectionStatus:', error);
      setIsProtected(false);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshAuth = () => {
    setIsLoading(true);
    checkProtectionStatus();
  };

  return { isProtected, isAuthenticated, isLoading, refreshAuth };
};
